/* TRIAGE DETERMINÍSTICO de scripts Sabre Scribe — SIN IA.
   Implementa la estrategia de docs/ESTRATEGIA_CONVERSION_SCRIPTS.md:
   1. Inventario léxico (ScribeParser)            4. Campos sensibles (PCI/PII)
   2. Clasificación de riesgo R1-R6 por comando   5. Mapeo comando → API real (Postman)
   3. Detección de huecos (defines sin uso, etc.) 6. Bloque de contexto para el Agente 1/2
   Todo corre local: ahorra tokens de IA y ordena el backlog antes de gastar agentes. */

import { normalizeReplacementChars, ScribeParser } from './parsers'
import { analyzeSstDiagnostics, summarizeDiagnostics, DIAGNOSTIC_META } from './sstDiagnostics'

/* ===== Tabla de clasificación de comandos GDS (de la biblia + estrategia) ===== */
const CMD_CLASSES = [
  // commit / cierre de transacción
  { re: /^(ER|ET)\b/i, clase: 'commit', label: 'Fin de transacción (persiste cambios en el PNR)' },
  { re: /^IR\b/i, clase: 'exec', label: 'Ignore — descarta cambios no guardados' },
  // emisión (crítico)
  { re: /^W[‡#¥]/i, clase: 'commit', label: 'EMISIÓN de documentos (crítico)' },
  // pricing
  { re: /^PQD/i, clase: 'write', label: 'Borra Price Quote (modifica pricing)' },
  { re: /^WP/i, clase: 'write', label: 'Pricing / re-cotización (crea PQ record)' },
  { re: /^\*PQ/i, clase: 'read', label: 'Lee Price Quote' },
  // venta / cancelación de segmentos
  { re: /^0/, clase: 'write', label: 'VENTA de segmento' },
  { re: /^X[0-9I]/i, clase: 'write', label: 'CANCELACIÓN de segmento' },
  // remarks: BORRADO/MODIFICACION por linea (¤ = change key, byte 0x01 en el fuente)
  { re: /^5[\d,\-*]+¤\s*$/, clase: 'write', label: 'BORRA remarks por numero de linea (¤) — patron reemplazo de bloque' },
  { re: /^5\d[\d,\-*]*¤./, clase: 'write', label: 'MODIFICA remark existente por linea (¤)' },
  // remarks / received-from
  { re: /^5/, clase: 'write', label: 'Escribe remark' },
  { re: /^6/, clase: 'write', label: 'Received-from' },
  // asignación de cliente (customer number): DK + 6 dígitos — entrada Sabre ESTÁNDAR
  { re: /^DK\d{4,7}\b/i, clase: 'write', label: 'Asigna DK (customer number) al PNR — entrada Sabre estándar' },
  // perfiles
  { re: /^NB/i, clase: 'write', label: 'Crea/edita perfil (STAR)' },
  { re: /^N\*/i, clase: 'read', label: 'Lee perfil (STAR)' },
  // colas
  { re: /^QP/i, clase: 'write', label: 'Encola PNR' },
  { re: /^Q/i, clase: 'exec', label: 'Operación de cola' },
  // lecturas de pantalla / PNR
  { re: /^\*/, clase: 'read', label: 'Lectura de pantalla/PNR' },
  // ayudas / informativos
  { re: /^(Y\/|F\*|FQ)/i, clase: 'exec', label: 'Consulta informativa' },
]

function classifyCommand(cmd) {
  const c = String(cmd || '').trim()
  for (const k of CMD_CLASSES) if (k.re.test(c)) return { clase: k.clase, label: k.label }
  return { clase: 'exec', label: 'Comando no catalogado (revisar manualmente)' }
}

/* ===== Mapeo comando → API real (colecciones Postman pública/privada). GAP = no existe aún. ===== */
const API_MAP = [
  { re: /^\*(A|N|P|I|H|S|R|T|-|\.)/i, api: 'GET /sabre/reservation · GET /sabre/remarks', gap: false },
  { re: /^\*PQ/i, api: 'HOY ✔ executeCommand (allowlist) · ideal: endpoint repriceQuote dedicado', gap: true },
  { re: /^5[\d,\-*]+¤\s*$/, api: 'POST /sabre/deleteremarks (runtime: directiva {deleteRemarks: match|range|all})', gap: false },
  { re: /^5\d[\d,\-*]*¤./, api: 'POST /sabre/modifyremarks', gap: false },
  { re: /^5/, api: 'POST /sabre/addremarks (runtime: catálogo skip=false)', gap: false },
  { re: /^6/, api: 'incluido en flujo addremarks/enhancedEndTransaction', gap: false },
  { re: /^(ER|ET)\b/i, api: 'POST /sabre/enhancedEndTransaction', gap: false },
  { re: /^IR\b/i, api: 'HOY ✔ executeCommand (allowlist) — comando IR · endpoint dedicado opcional', gap: true },
  { re: /^(WP|PQD)/i, api: 'HOY ✔ executeCommand (allowlist) · ideal: repriceQuote backend (*PQ→PQD→WP atómico)', gap: true },
  { re: /^0/, api: 'POST /sabre/enhancedAirBook · enhancedVehBook', gap: false },
  { re: /^X[0-9I]/i, api: 'HOY ✔ executeCommand (allowlist) · ideal: exponer EnhancedCancelBooking (API privada) con scope', gap: true },
  { re: /^DK\d{4,7}\b/i, api: 'HOY ✔ executeCommand (allowlist) — DK es entrada Sabre estándar de asignación de cliente', gap: true },
  { re: /^N(\*|B)/i, api: 'GET /sabre/freeTAS · POST /sabre/updateProfile', gap: false },
  { re: /^QP?/i, api: 'HOY ✔ executeCommand (allowlist) — QP/colas · endpoint dedicado opcional', gap: true },
]

function mapApi(cmd) {
  const c = String(cmd || '').trim()
  for (const k of API_MAP) if (k.re.test(c)) return { api: k.api, gap: k.gap }
  return { api: 'GET /sabre/executeCommand (ÚLTIMO RECURSO — allowlist por script)', gap: true }
}

/* ===== Families semánticas (Catálogo de Bloques Lógicos v1.0 + extensiones Boston) =====
   El triage etiqueta cada SEND con su family DETERMINÍSTICAMENTE para que el Agente 1
   la reciba como dato verificado (no la infiere). Extensiones Boston: pnr.received (6),
   pnr.dk (DK######), profile (N-star y NB) — documentadas en la tabla de equivalencias. */
const FAMILY_RULES = [
  { re: /^(ER|ET|E)\b/i, family: 'pnr.commit' },
  { re: /^(IR|I)\s*$/i, family: 'pnr.ignore' },
  { re: /^\*/, family: 'pnr.read' },
  { re: /^5/, family: 'pnr.remark' },
  { re: /^[34]/, family: 'pnr.ssr' },
  { re: /^9/, family: 'pnr.contact' },
  { re: /^-/, family: 'pnr.name' },
  { re: /^(0|X[0-9I])/i, family: 'pnr.segment' },
  { re: /^(WP|PQD)/i, family: 'pricing' },
  { re: /^(W[‡#¥-]|7)/i, family: 'ticketing' },
  { re: /^Q/i, family: 'queue' },
  { re: /^(¤A|SI\d|SO\b)/i, family: 'area' },
  { re: /^VI/i, family: 'pnr.read' },
  { re: /^CTC[EM]/i, family: 'pnr.ssr' },
  { re: /^ACOTH/i, family: 'ticketing' },
  { re: /^6/, family: 'pnr.received' },
  { re: /^DK\d{4,7}\b/i, family: 'pnr.dk' },
  { re: /^N(\*|B)/i, family: 'profile' },
]

export function classifyFamily(cmd) {
  let clean = String(cmd || '').trim()
  if (clean === '') return 'keys' // solo teclas ({CLEAR}/{ENTER}): sin efecto PNR
  // los templates pueden arrancar con variables ([3O4]CTCE/...): clasificar lo que sigue
  const sinVars = clean.replace(/^(\[[^\]]+\]\s*)+/, '').trim()
  if (sinVars !== '' && sinVars !== clean) clean = sinVars
  for (const rule of FAMILY_RULES) if (rule.re.test(clean)) return rule.family
  return 'unknown'
}

/* ===== Campos sensibles (PCI / PII) sobre los DEFINE ===== */
const SENSITIVE_RULES = [
  { re: /(NUMCC|NRO?TARJ|CARDNUM|\bPAN\b)/i, tipo: 'PCI', detalle: 'número de tarjeta — SOLO tokenización Akita (tokenize→sendPayment), nunca en claro' },
  { re: /(CODSEG|\bCVV\b|\bCVC\b|SEGURIDAD)/i, tipo: 'PCI', detalle: 'código de seguridad — PROHIBIDO capturar/persistir' },
  { re: /(TARJ|CARD)/i, tipo: 'PCI', detalle: 'dato de tarjeta — enmascarar (VI…1111)' },
  { re: /(VENCIM|EXPIR)/i, tipo: 'PCI', detalle: 'vencimiento de tarjeta — solo junto a token' },
  { re: /^(MM|YY)(#[DC])?$/i, tipo: 'PCI', soloNombre: true, detalle: 'vencimiento de tarjeta (MM/YY) — solo junto a token' },
  { re: /(CUIL|CUIT|\bDNI\b|PASAPORTE|PASSPORT|\bRFC\b|NUMDOC|DOCUMENTO)/i, tipo: 'PII', detalle: 'documento de identidad — minimizar y no loguear' },
]

function detectSensitive(defines) {
  const found = []
  for (const d of defines) {
    const hay = `${d.variable} ${d.description || ''}`
    for (const r of SENSITIVE_RULES) {
      if (r.re.test(r.soloNombre ? d.variable : hay)) {
        found.push({ campo: d.variable, tipo: r.tipo, detalle: r.detalle })
        break
      }
    }
    // heurística PAN extra: numérico de 13-19 sin regla previa
    if (!found.some((f) => f.campo === d.variable) && /^(NUM|N)$/i.test(d.type || '') && d.length >= 13 && d.length <= 19 && /CC|TARJ|CARD/i.test(hay)) {
      found.push({ campo: d.variable, tipo: 'PCI', detalle: 'posible PAN por tipo/longitud' })
    }
  }
  return found
}


/* ===== Preproceso: variantes reales de .sst legacy que el parser base no cubre ===== */
export function preprocessSst(s) {
  // 1) Chevrones CP437 decodificados como windows-1252: » (0xAF) → "¯", « (0xAE) → "®"
  if (/¯.+®/.test(s)) s = s.replace(/¯/g, '»').replace(/®/g, '«')
  // 2) Chevrones como U+FFFD u otros caracteres de control: detectar el delimitador
  //    dominante y reemplazarlo alternando »/« por línea (cuenta par)
  const counts = {}
  for (const ch of s) {
    const code = ch.charCodeAt(0)
    if ((code === 0xfffd || code <= 0x1f || (code >= 0x7f && code <= 0xa0)) && ch !== '\n' && ch !== '\r' && ch !== '\t') {
      counts[ch] = (counts[ch] || 0) + 1
    }
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
  if (best && best[1] >= 2) {
    const esc = best[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    s = s
      .split(/\r?\n/)
      .map((line) => {
        const n = line.split(best[0]).length - 1
        if (n >= 2 && n % 2 === 0) {
          let i = 0
          return line.replace(new RegExp(esc, 'g'), () => (i++ % 2 === 0 ? '»' : '«'))
        }
        return line
      })
      .join('\n')
  }
  // 3) SUBROUTINE/ENDSUBROUTINE (forma larga) → SUB/ENDSUB que el parser entiende
  s = s.replace(/^(\s*)SUBROUTINE\s+/gim, '$1SUB ').replace(/^(\s*)ENDSUBROUTINE\b/gim, '$1ENDSUB')
  // 4) Varios DEFINE/DEFAULT en una misma línea → uno por línea (el parser toma solo el 1º)
  s = s
    .split(/\r?\n/)
    .map((line) => {
      if (/^\s*DEFINE\s*\[/i.test(line)) {
        const parts = [...line.matchAll(/DEFINE\s*\[[^\]]*\]/gi)].map((m) => m[0])
        if (parts.length > 1) return parts.join('\n')
      }
      if (/^\s*DEFAULT\s*\[/i.test(line)) {
        const parts = [...line.matchAll(/DEFAULT\s*\[[^\]]*\]\s*=\s*("[^"]*"|\[[^\]]*\]|[^\s]+)/gi)].map((m) => m[0])
        if (parts.length > 1) return parts.join('\n')
      }
      return line
    })
    .join('\n')
  return s
}

/* ===== Nombres amigables: "perfil_corporativo_udid" → "Perfil Corporativo UDID" ===== */
export function humanizeScriptName(value) {
  const base = String(value || '')
    .replace(/\.(sst|ssc|sbr)$/i, '')
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!base) return 'Script'
  return base
    .split(' ')
    .map((w) => (w.length <= 4 && w === w.toUpperCase() && /[A-Z]/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
}

/* ===== Dependencias entre scripts: CHAIN (encadena y no vuelve) / SPAWN (corre y vuelve).
   Si aparecen, el análisis necesita TAMBIÉN el contenido de esos scripts. ===== */
export function detectScriptDependencies(source) {
  const src = String(source || '')
  const items = []
  const seen = new Set()
  for (const m of src.matchAll(/\b(CHAIN|SPAWN)\b[\s:]+(?:F\s*=\s*)?"?([\w.\-]{2,60})"?/gi)) {
    const destino = m[2].trim()
    const key = `${m[1].toUpperCase()} ${destino.toUpperCase()}`
    if (destino && !seen.has(key)) {
      seen.add(key)
      items.push({ comando: m[1].toUpperCase(), destino })
    }
  }
  const usaExternal = /^\s*EXTERNAL\b/im.test(src)
  return { items, usaExternal }
}

/* ===== Alertas al usuario: ventanas WINDOW usadas como aviso/confirmación
   (típicamente dentro de un IF, ej: "LA TARIFA ES DIFERENTE, DESEA CONTINUAR"). ===== */
const ALERT_KEYWORDS = /(DESEA\s+CONTINUAR|DIFEREN|ATENCI|ADVERTENC|ALERTA|AVISO|CUIDADO|VERIFIC|NO\s+SE\s+PUDO|FALTA|ERROR|IMPORTANTE|CONFIRMA)/i

export function detectScriptAlerts(source) {
  const src = String(source || '')
  const lines = src.split(/\r?\n/)
  // labels de botones: [@BTN_XXX] = "TEXTO"
  const buttonLabels = {}
  for (const m of src.matchAll(/\[(@BTN[^\]=]*)\]\s*=\s*"([^"]+)"/g)) {
    buttonLabels[`[${m[1]}]`] = m[2].trim()
  }
  const alerts = []
  let ifDepth = 0
  let win = null
  for (let i = 0; i < lines.length; i += 1) {
    const t = lines[i].trim()
    if (!win) {
      if (/^IF\b[\s\S]*\bTHEN\b/i.test(t)) { ifDepth += 1; continue }
      if (/^ENDIF\b/i.test(t)) { ifDepth = Math.max(0, ifDepth - 1); continue }
      if (/^WINDOW\b/i.test(t)) {
        const hm = t.match(/H\s*=\s*"([^"]*)"/i)
        win = { titulo: hm ? hm[1] : '', lineas: [], botones: [], condicional: ifDepth > 0, linea: i + 1 }
      }
      continue
    }
    if (/^ENDWINDOW\b/i.test(t)) {
      const texto = win.lineas.join(' ').trim()
      const esAlerta = (win.condicional || ALERT_KEYWORDS.test(texto) || ALERT_KEYWORDS.test(win.titulo)) && texto !== ''
      if (esAlerta) {
        alerts.push({
          titulo: win.titulo,
          mensaje: win.lineas.slice(0, 4).join(' · ').slice(0, 220),
          botones: win.botones,
          condicional: win.condicional,
          linea: win.linea,
        })
      }
      win = null
      continue
    }
    const btns = t.match(/\[@BTN[^\]]*\]/g)
    if (btns) {
      btns.forEach((b) => {
        const label = buttonLabels[b]
        if (label && !win.botones.includes(label)) win.botones.push(label)
      })
      continue
    }
    if (t && !/^[-=_·*\s]+$/.test(t) && !/^\[[^\]]+\]$/.test(t)) {
      win.lineas.push(t.replace(/\s+/g, ' '))
    }
  }
  return alerts
}

/* Campos capturados en WINDOWs (para el chequeo de cobertura fuente → data.json). */
export function extractWindowFields(source) {
  const lines = String(source || '').split(/\r?\n/)
  const fields = []
  let inWindow = false
  for (const line of lines) {
    if (/^\s*;/.test(line)) continue
    if (/^\s*WINDOW\b/i.test(line)) { inWindow = true; continue }
    if (/^\s*ENDWINDOW\b/i.test(line)) { inWindow = false; continue }
    if (!inWindow) continue
    const noQuotes = line.replace(/"[^"]*"/g, ' ')
    for (const m of noQuotes.matchAll(/\[([A-Za-z_][A-Za-z0-9_#]*)\]/g)) {
      const name = m[1].toUpperCase()
      if (!name.startsWith('@') && !fields.includes(name)) fields.push(name)
    }
  }
  return fields
}

/* ===== Triage principal ===== */
export function triageSst(source, filename, options = {}) {
  const src = preprocessSst(normalizeReplacementChars(String(source || '')))
  const program = new ScribeParser().parse(src)

  // -- comandos (EmulatorCmd + WRITE a SABRE:)
  const comandos = []
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (n.kind === 'EmulatorCmd') comandos.push(n.command)
      if (n.kind === 'Raw' && n.raw) for (const m of String(n.raw).matchAll(/»([^«]+)«/g)) comandos.push(m[1])
      if (n.kind === 'Write' && n.target === 'SABRE') comandos.push((n.fragments || []).map((f) => (f.kind === 'Literal' ? f.value : `[${f.name}]`)).join(''))
      if (n.kind === 'If') {
        walk(n.thenBody)
        ;(n.elseIfs || []).forEach((e) => walk(e.body))
        walk(n.elseBody)
      }
      if (n.kind === 'Menu') (n.items || []).forEach((it) => walk(it.body))
      if (n.kind === 'Sub' || n.kind === 'Repeat') walk(n.body)
    }
  }
  walk(program.nodes)

  // coleccion recursiva de todos los nodos (los IF anidan Window/Menu en sus cuerpos)
  const allNodes = []
  const collect = (nodes) => {
    for (const n of nodes || []) {
      allNodes.push(n)
      if (n.kind === 'If') {
        collect(n.thenBody)
        ;(n.elseIfs || []).forEach((e) => collect(e.body))
        collect(n.elseBody)
      }
      if (n.kind === 'Menu') (n.items || []).forEach((it) => collect(it.body))
      if (n.kind === 'Sub' || n.kind === 'Repeat') collect(n.body)
    }
  }
  collect(program.nodes)

  const comandosClasificados = comandos.map((cmd) => {
    const limpio = String(cmd).replace(/\{[A-Z_]+\}/g, '').trim()
    return { cmd: String(cmd).trim(), ...classifyCommand(limpio), ...mapApi(limpio), family: classifyFamily(limpio) }
  })

  // -- nivel de riesgo
  const clases = new Set(comandosClasificados.map((c) => c.clase))
  const tieneForms = allNodes.some((n) => n.kind === 'Window' || n.kind === 'Menu')
  let nivel = 'R1 · Solo lectura'
  if (tieneForms) nivel = 'R2 · Lectura y visualización'
  if (clases.has('exec')) nivel = 'R3 · Ejecución de comandos'
  if (clases.has('write')) nivel = 'R4 · Modificación de reserva'
  if (clases.has('commit')) nivel = 'R5 · Automatización operativa (cierra transacción)'
  // mixto: hay decisión humana (ventana/menú/botones) Y escritura/commit
  const tieneBranches = allNodes.some((n) => n.kind === 'If') || /\[@BTN_PRSD\]/.test(src)
  if ((clases.has('write') || clases.has('commit')) && tieneForms && tieneBranches) {
    nivel = 'R6 · Mixto (clasificar POR RAMA: visualización + ' + (clases.has('commit') ? 'commit' : 'escritura') + ')'
  }

  // -- huecos
  const cuerpoSinDefines = src
    .split(/\r?\n/)
    .filter((l) => !/^\s*(DEFINE|DEFAULT)\s*\[/i.test(l))
    .join('\n')
  const definesSinUso = program.defines.filter((d) => !cuerpoSinDefines.includes(`[${d.variable}]`)).map((d) => d.variable)

  const labelsDef = new Set(program.labels.map((l) => l.name))
  const gotoTargets = []
  const scan = (nodes) => {
    for (const n of nodes || []) {
      if (n.kind === 'Goto' || n.kind === 'Call') gotoTargets.push(n.label)
      if (n.kind === 'Raw' && n.raw) for (const m of String(n.raw).matchAll(/\b(?:GOTO|CALL)\s+([A-Za-z_][\w]*)/gi)) gotoTargets.push(m[1])
      if (n.kind === 'If') {
        scan(n.thenBody)
        ;(n.elseIfs || []).forEach((e) => scan(e.body))
        scan(n.elseBody)
      }
      if (n.kind === 'Menu') (n.items || []).forEach((it) => scan(it.body))
      if (n.kind === 'Sub' || n.kind === 'Repeat') scan(n.body)
    }
  }
  scan(program.nodes)
  const gotosRotos = [...new Set(gotoTargets.filter((t) => t && !labelsDef.has(t) && !program.subs.some((s) => s.name === t)))]
  const labelsHuerfanos = [...labelsDef].filter((l) => !gotoTargets.includes(l))
  // Dependencias: nodos del parser + barrido regex del fuente completo (cubre CHAIN/SPAWN anidados en IFs)
  const depsRegex = detectScriptDependencies(src)
  const dependencias = [...new Set([
    ...program.nodes.filter((n) => n.kind === 'Chain' || n.kind === 'Spawn').map((n) => `${n.kind.toUpperCase()} ${n.scriptName}`),
    ...depsRegex.items.map((d) => `${d.comando} ${d.destino}`),
  ])]
  const alertas = detectScriptAlerts(src)
  const botonesSinRama = [...src.matchAll(/\[@BTN[^\]=]*\]\s*=\s*"([^"]+)"/g)]
    .map((m) => m[1])
    .filter((b) => !new RegExp(`\\[@BTN_PRSD\\]\\s*=\\s*"?${b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(src))

  const sensibles = detectSensitive(program.defines)
  const apis = [...new Set(comandosClasificados.map((c) => c.api))]
  const families = [...new Set(comandosClasificados.map((c) => c.family))]
  const camposVentana = extractWindowFields(src)
  const gaps = [...new Set(comandosClasificados.filter((c) => c.gap).map((c) => c.api))]

  const huecos = { definesSinUso, labelsHuerfanos, gotosRotos, dependencias, botonesSinRama }
  const incompleto = definesSinUso.length > Math.max(5, program.defines.length * 0.4)

  // Validaciones deterministas formales (MISSING_CSV_FILE, MISSING_GOTO_TARGET, etc.) sobre el fuente original
  const diagnostics = analyzeSstDiagnostics(String(source || ''), {
    providedFiles: options.providedFiles || [],
    hasClipboardAdapter: options.hasClipboardAdapter,
  })
  const diagnosticsSummary = summarizeDiagnostics(diagnostics)

  return {
    filename: filename || '',
    nivel,
    stats: {
      defines: program.defines.length,
      comandos: comandos.length,
      ventanas: allNodes.filter((n) => n.kind === 'Window').length,
      menus: allNodes.filter((n) => n.kind === 'Menu').length,
      labels: program.labels.length,
      subs: program.subs.length,
      noParseado: program.unparsed.length,
      alertas: alertas.length,
    },
    comandos: comandosClasificados,
    huecos,
    diagnostics,
    diagnosticsSummary,
    sensibles,
    apis,
    families,
    camposVentana,
    gaps,
    incompleto,
    alertas,
    dependenciasDetalle: depsRegex.items,
    usaExternal: depsRegex.usaExternal,
    resumen:
      `${nivel}. ${comandos.length} comandos GDS, ${program.defines.length} variables` +
      (sensibles.length ? `, ${sensibles.length} campo(s) SENSIBLE(S) (${sensibles.map((s) => s.tipo).join('/')})` : '') +
      (alertas.length ? `. ⚠ ${alertas.length} alerta(s)/aviso(s) al usuario` : '') +
      (incompleto ? `. ⚠ POSIBLE FUENTE INCOMPLETO: ${definesSinUso.length} variables definidas sin uso en el cuerpo` : '') +
      (dependencias.length ? `. Depende de: ${dependencias.join(', ')}` : ''),
  }
}

/* ===== Bloque de contexto para inyectar al prompt del Agente 1/2 (ahorra tokens) ===== */
export function triageToPromptBlock(t) {
  if (!t) return ''
  const lines = []
  lines.push('=== TRIAGE_PREVIO (inventario determinístico ya verificado — NO lo re-derives, usalo) ===')
  lines.push(`ARCHIVO: ${t.filename} · RIESGO: ${t.nivel}`)
  lines.push(`STATS: ${t.stats.defines} defines · ${t.stats.comandos} comandos · ${t.stats.ventanas} ventanas · ${t.stats.menus} menús · ${t.stats.subs} subs · ${t.stats.noParseado} líneas no parseadas`)
  lines.push('COMANDOS (orden de aparición → clase → [family] → API destino; ×N = repetido):')
  const vistos = new Map()
  t.comandos.forEach((c) => {
    const k = `${c.cmd} → ${c.clase} → [${c.family || 'unknown'}] → ${c.api}`
    vistos.set(k, (vistos.get(k) || 0) + 1)
  })
  let i = 0
  vistos.forEach((n, k) => lines.push(`  ${++i}. ${k}${n > 1 ? ` (×${n})` : ''}`))
  if (Array.isArray(t.families) && t.families.length) {
    lines.push(`FAMILIES (inventario verificado — usá EXACTAMENTE estas en los bloques command del IR, NO las re-derives): ${t.families.join(', ')}`)
  }
  if (t.sensibles.length) {
    lines.push('CAMPOS SENSIBLES (marcá sensitive en el IR y NUNCA a remark/log en claro):')
    t.sensibles.forEach((s) => lines.push(`  - [${s.campo}] ${s.tipo}: ${s.detalle}`))
  }
  if (t.alertas && t.alertas.length) {
    lines.push(`ALERTAS AL USUARIO (${t.alertas.length}) — el script muestra estos avisos/confirmaciones; LISTALAS SIEMPRE en la interpretación y respetalas en el data.json (confirmaciones antes de continuar):`)
    t.alertas.forEach((a, idx) => {
      lines.push(`  ${idx + 1}. ${a.condicional ? '[condicional] ' : ''}"${a.titulo || 'sin título'}": ${a.mensaje}${a.botones.length ? ` → botones: ${a.botones.join(' / ')}` : ''}`)
    })
  }
  if (t.huecos.definesSinUso.length) lines.push(`HUECOS — defines sin uso (${t.huecos.definesSinUso.length}): ${t.huecos.definesSinUso.slice(0, 15).join(', ')}${t.huecos.definesSinUso.length > 15 ? ` … (+${t.huecos.definesSinUso.length - 15} más)` : ''}`)
  if (t.huecos.gotosRotos.length) lines.push(`HUECOS — GOTO/CALL sin destino: ${t.huecos.gotosRotos.join(', ')}`)
  if (t.huecos.botonesSinRama.length) lines.push(`HUECOS — botones sin rama: ${t.huecos.botonesSinRama.join(', ')}`)
  if (t.huecos.dependencias.length) lines.push(`DEPENDENCIAS EXTERNAS (CHAIN/SPAWN): ${t.huecos.dependencias.join(', ')} — si el contenido de esos scripts NO está incluido en este análisis, listalos en open_questions y pedile al usuario que los suba para analizarlos juntos.`)
  if (t.incompleto) lines.push('⚠ EL FUENTE PARECE INCOMPLETO: declaralo en open_questions y bajá confidence. NO inventes la parte faltante.')
  if (Array.isArray(t.diagnostics) && t.diagnostics.length) {
    const s = t.diagnosticsSummary || { error: 0, warning: 0, info: 0 }
    lines.push(`VALIDACIONES DETERMINISTAS (${t.diagnostics.length}: ${s.error} error · ${s.warning} warning · ${s.info} info) — respetalas SIEMPRE; NO generes selects vacíos, botones sin acción ni compares contra valores fuera de catálogo:`)
    t.diagnostics.slice(0, 40).forEach((d) => {
      const meta = DIAGNOSTIC_META[d.code]
      const tag = meta ? meta.title : d.code
      lines.push(`  - [${(d.severity || 'info').toUpperCase()}] ${d.code} (L${d.sourceLine || '?'}) ${tag}: ${d.message}`)
    })
    if (t.diagnostics.length > 40) lines.push(`  … (+${t.diagnostics.length - 40} más)`)
  }
  lines.push('=== FIN TRIAGE_PREVIO ===')
  return lines.join('\n')
}


/* ===== IR → texto humano: "esto hace el script" (para confirmación del usuario) ===== */
function isValidIRSummary(summary) {
  const value = String(summary || '').trim()
  if (!value) return false
  const normalized = value.toLowerCase()
  if (normalized === '(el intÃ©rprete no devolviÃ³ resumen)') return false
  if (normalized === '(el intérprete no devolvió resumen)') return false
  if (normalized === 'no disponible') return false
  if (normalized === 'no se pudo determinar') return false
  if (/^<[^>]+>$/.test(value)) return false
  return true
}

export function getRequiredIRSummary(ir) {
  const metaSummary = ir && ir.meta ? ir.meta.summary : ''
  const rootSummary = ir ? ir.summary : ''
  const summary = String(metaSummary || rootSummary || '').trim()
  if (!isValidIRSummary(summary)) {
    throw new Error('Agente 1 no entregó la descripción funcional obligatoria en meta.summary.')
  }
  if (ir && typeof ir === 'object') {
    if (!ir.meta || typeof ir.meta !== 'object' || Array.isArray(ir.meta)) ir.meta = {}
    ir.meta.summary = summary
    ir.summary = summary
  }
  return summary
}

export function irToHumanText(ir) {
  const meta = (ir && ir.meta) || {}
  const L = []
  if (meta.title) L.push('SCRIPT: ' + meta.title)
  L.push(getRequiredIRSummary(ir))
  if (Array.isArray(meta.reads) && meta.reads.length) L.push('')
  if (Array.isArray(meta.reads) && meta.reads.length) L.push('LEE: ' + meta.reads.join('; '))
  if (meta.writes) L.push('ESCRIBE: ' + meta.writes)
  const rc = ir && ir.remarks_cleanup
  if (rc) {
    const det = [rc.range ? 'líneas ' + rc.range : '', rc.match ? 'los que matcheen /' + rc.match + '/' : ''].filter(Boolean).join(' · ')
    L.push('BORRA REMARKS PREVIOS: reemplaza su bloque anterior' + (det ? ' (' + det + ')' : '') + (rc.note ? ' — ' + rc.note : ''))
  }
  if (meta.does_not) L.push('NO HACE: ' + meta.does_not)
  if (meta.inputs) L.push('CAMPOS DEL FORM: ' + meta.inputs)
  if (ir && ir.source && ir.source.confidence) {
    L.push('')
    L.push('CONFIANZA: ' + ir.source.confidence + (ir.source.kind === 'ssc' ? ' (compilado .ssc — flujo parcial, revisá y completá)' : ''))
  }
  const oq = (ir && ir.open_questions) || []
  if (oq.length) {
    L.push('')
    L.push('DUDAS DEL INTÉRPRETE:')
    oq.forEach((q) => L.push('• ' + (typeof q === 'string' ? q : JSON.stringify(q))))
  }
  return L.join('\n')
}

/* ===== FICHA.md (documentación obligatoria del script antes de publicar) ===== */
export function buildFichaMd({ triage, ir, dataJson, name, uuid, warnings }) {
  const t = triage || null
  const meta = (ir && ir.meta) || {}
  const hoy = new Date().toISOString().slice(0, 10)
  const L = []
  L.push(`# FICHA — ${name || (dataJson && dataJson.title) || 'script'}`)
  L.push('')
  L.push(`Generada automáticamente el ${hoy}. UUID: \`${uuid || 'pendiente'}\``)
  L.push('')
  L.push(`## Resumen funcional`)
  L.push(getRequiredIRSummary(ir))
  L.push('')
  if (t) {
    L.push(`## Clasificación de riesgo`)
    L.push(t.nivel + (t.incompleto ? ' — ⚠ fuente posiblemente incompleto' : ''))
    L.push('')
    L.push(`## Comandos detectados`)
    L.push('| # | Comando | Clase | API destino |')
    L.push('|---|---------|-------|-------------|')
    t.comandos.forEach((c, i) => L.push(`| ${i + 1} | \`${c.cmd.replace(/\|/g, '\\|')}\` | ${c.clase} | ${c.api.replace(/\|/g, '\\|')} |`))
    L.push('')
  }
  L.push(`## Datos`)
  L.push(`- LEE: ${(meta.reads || []).join('; ') || '(ver IR)'}`)
  L.push(`- MUESTRA: ${meta.inputs || '(ver forms del data.json)'}`)
  L.push(`- MODIFICA: ${meta.writes || '(ver command_plan/remarks)'}`)
  L.push(`- NO HACE: ${meta.does_not || '-'}`)
  if (t && t.sensibles.length) {
    L.push('')
    L.push(`## Campos sensibles (PCI/PII)`)
    t.sensibles.forEach((s) => L.push(`- **${s.campo}** (${s.tipo}): ${s.detalle}`))
  }
  if (t) {
    L.push('')
    L.push(`## APIs necesarias`)
    t.apis.forEach((a) => L.push(`- ${a}`))
    if (t.gaps.length) {
      L.push('')
      L.push(`### Sin endpoint dedicado — hoy se resuelve con executeCommand + allowlist (recomendado crear)`)
      t.gaps.forEach((g) => L.push(`- ${g}`))
    }
    const h = t.huecos
    if (h.definesSinUso.length || h.gotosRotos.length || h.dependencias.length || h.botonesSinRama.length) {
      L.push('')
      L.push(`## Huecos detectados / preguntas abiertas`)
      if (h.definesSinUso.length) L.push(`- Defines sin uso (${h.definesSinUso.length}): ${h.definesSinUso.join(', ')}`)
      if (h.gotosRotos.length) L.push(`- GOTO/CALL rotos: ${h.gotosRotos.join(', ')}`)
      if (h.botonesSinRama.length) L.push(`- Botones sin rama: ${h.botonesSinRama.join(', ')}`)
      if (h.dependencias.length) L.push(`- Dependencias externas: ${h.dependencias.join(', ')}`)
    }
  }
  if (ir && Array.isArray(ir.open_questions) && ir.open_questions.length) {
    L.push('')
    L.push(`## Open questions del IR`)
    ir.open_questions.forEach((q) => L.push(`- ${typeof q === 'string' ? q : JSON.stringify(q)}`))
  }
  if (warnings && warnings.length) {
    L.push('')
    L.push(`## Warnings de los agentes`)
    warnings.forEach((w) => L.push(`- ${w}`))
  }
  L.push('')
  L.push(`## Nivel de certeza`)
  L.push(`${(ir && ir.source && ir.source.confidence) || 'medio'} (fuente: ${(ir && ir.source && ir.source.kind) || 'desconocida'})`)
  L.push('')
  L.push(`## Validación`)
  L.push('- [ ] Validado por usuario operativo: ____________  fecha: ______')
  L.push('- [ ] Aprobado Dev (Revisión Dev): ____________  fecha: ______')
  L.push('')
  return L.join('\n')
}
